import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth.store'
import { Button } from '@heroui/react'
import { LayoutDashboard, Users, UsersRound, FolderKanban, BarChart3, Settings, LogOut, Search, Camera, Menu, X, MonitorPlay, Sun, Moon, Activity } from 'lucide-react'
import CommandPalette from '../components/CommandPalette'
import NotificationsBell from '../components/NotificationsBell'
import { useNotificationSocket } from '../hooks/useNotificationSocket'
import { useTheme } from 'next-themes'

export default function DashboardLayout() {
  // N8: global notification socket (badge + toast); polling is the fallback.
  useNotificationSocket()
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const viewTimezone = useAuthStore((state) => state.viewTimezone)
  const setViewTimezone = useAuthStore((state) => state.setViewTimezone)
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [tzQuery, setTzQuery] = useState('')
  const { theme, setTheme, resolvedTheme, systemTheme } = useTheme()
  const effectiveTheme = resolvedTheme ?? (theme === 'system' ? systemTheme : theme)
  const toggleTheme = () => setTheme(effectiveTheme === 'dark' ? 'light' : 'dark')

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [mobileMenuOpen])

  const intlWithSupportedValues = Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  const timezones = typeof intlWithSupportedValues.supportedValuesOf === 'function' ? intlWithSupportedValues.supportedValuesOf('timeZone') : [
    'UTC', 'Africa/Cairo', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Dubai', 'Asia/Tokyo'
  ]

  const normalizedTzQuery = tzQuery.trim().toLowerCase()
  const filteredTimezones = normalizedTzQuery
    ? timezones.filter((tz: string) => tz.toLowerCase().includes(normalizedTzQuery))
    : timezones
  const visibleTimezones = filteredTimezones.slice(0, 50)
  // Keep the stored string selectable even when it is filtered out.
  const visibleWithSelected = viewTimezone && !visibleTimezones.includes(viewTimezone)
    ? [viewTimezone, ...visibleTimezones]
    : visibleTimezones

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const menuItems = [
    { icon: LayoutDashboard, label: 'Overview', path: '/' },
    ...(user?.role !== 'employee' ? [
      { icon: Users, label: 'Employees', path: '/employees' },
      { icon: UsersRound, label: 'Teams', path: '/teams' },
      { icon: MonitorPlay, label: 'Live View', path: '/live' },
      { icon: BarChart3, label: 'Reports', path: '/reports' },
      { icon: Camera, label: 'Screenshots', path: '/screenshots' },
      { icon: Settings, label: 'Settings', path: '/settings' },
    ] : []),
    { icon: FolderKanban, label: 'Projects', path: '/projects' },
    // Activity can be seen by both employees (their own) and admins
    { icon: Activity, label: 'Activity', path: '/activity' }, 
  ]

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden transition-colors duration-200">
      {/* Skip link: invisible until focused, jumps straight to the page content. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:top-2 focus:left-2 focus:bg-content2 focus:text-foreground focus:px-4 focus:py-2 focus:rounded-md focus:ring-2 focus:ring-spotify-green"
      >
        تخطَّ إلى المحتوى
      </a>
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black dark:bg-white/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-content1 border-r border-divider flex flex-col justify-between transform transition-transform duration-200 ease-in-out md:hidden ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div>
          <div className="p-6 flex items-center justify-between">
            <img src="/logo.png" alt="WorkFlow" className="h-8 w-auto" onError={(e) => { e.currentTarget.style.display = 'none' }} />
            <button onClick={() => setMobileMenuOpen(false)} className="text-default-500 hover:text-foreground" aria-label="Close menu">
              <X size={20} />
            </button>
          </div>
          
          <nav className="px-4 py-2 space-y-1" aria-label="Mobile navigation">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-sm font-medium ${
                    isActive 
                      ? 'bg-content2 text-foreground' 
                      : 'text-default-500 hover:text-foreground hover:bg-content2/50'
                  }`
                }
              >
                <item.icon size={20} />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="w-64 bg-content1 border-r border-divider flex-col justify-between hidden md:flex">
        <div>
          <div className="p-6 flex items-center gap-3">
            <img src="/logo.png" alt="WorkFlow" className="h-8 w-auto" onError={(e) => { e.currentTarget.style.display = 'none' }} />
          </div>
          
          <nav className="px-4 py-2 space-y-1" aria-label="Sidebar navigation">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-sm font-medium ${
                    isActive 
                      ? 'bg-content2 text-foreground' 
                      : 'text-default-500 hover:text-foreground hover:bg-content2/50'
                  }`
                }
              >
                <item.icon size={20} />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navbar */}
        <header className="h-16 flex items-center justify-between px-6 bg-background/80 backdrop-blur-md sticky top-0 z-10 border-b border-divider">
          <div className="flex items-center flex-1 gap-4">
            <button
              className="md:hidden text-default-500 hover:text-foreground"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
            <div className="relative w-full max-w-md hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-default-400" size={18} />
              <input 
                type="text" 
                placeholder="Search projects, users... (Cmd+K)" 
                className="w-full bg-content2 text-foreground placeholder-default-400 border-none rounded-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm hover:bg-content3"
                aria-label="Search"
                readOnly
                onClick={() => {
                  const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
                  window.dispatchEvent(event);
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={tzQuery}
                onChange={(e) => setTzQuery(e.target.value)}
                placeholder="بحث timezone…"
                className="bg-content2 text-default-600 border border-divider rounded-md py-1.5 px-2 text-xs w-28 focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-default-400"
                aria-label="Search timezones"
              />
              <select
                value={viewTimezone}
                onChange={(e) => setViewTimezone(e.target.value)}
                className="bg-content2 text-default-600 border border-divider rounded-md py-1.5 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer max-w-44"
                aria-label="Select timezone"
              >
                {visibleWithSelected.length === 0 ? (
                  <option value="" disabled>لا نتائج</option>
                ) : (
                  visibleWithSelected.map((tz: string) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))
                )}
              </select>
              <span
                className="text-[11px] text-default-400 whitespace-nowrap"
                title={normalizedTzQuery ? `مطابق للبحث: ${filteredTimezones.length}` : undefined}
              >
                {visibleTimezones.length} من {timezones.length}
              </span>
            </div>

            <Button isIconOnly variant="ghost" className="rounded-full text-default-500 hover:text-foreground" onPress={toggleTheme} aria-label="Toggle theme">
              {effectiveTheme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </Button>

            <NotificationsBell />
            
            {/* Minimal Custom Dropdown since HeroUI Dropdown API might differ in v3 */}
            <div className="flex items-center gap-3 ml-2 pl-4 border-l border-divider cursor-pointer group">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm shrink-0 shadow-lg">
                {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{user?.full_name || 'Admin User'}</p>
                <p className="text-xs text-default-500">{user?.role || 'Admin'}</p>
              </div>
              <Button isIconOnly variant="ghost" onPress={handleLogout} className="ml-2 bg-content2 text-default-500 hover:text-foreground hover:bg-content3" aria-label="Log out">
                <LogOut size={16} />
              </Button>
            </div>
          </div>
        </header>

        {/* Command Palette */}
        <CommandPalette />

        {/* Page Content */}
        <main id="main-content" tabIndex={-1} className="flex-1 overflow-auto p-6 relative print-area">
            {/* Background gradient hint */}
            <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-primary/10 to-transparent pointer-events-none -z-10" />
            <Outlet />
        </main>
      </div>
    </div>
  )
}
